import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import ToolTip, { IProps as IToolTipProps } from 'Components/new/tool-tip'
import { Wrapper } from 'lib/noop-wrapper'
import { unstable_batchedUpdates } from 'react-dom'
import batchedUpdates from 'lib/batched-updates'

interface IItemProps {
  onChange(isIntersecting: boolean): void
  tag?: keyof HTMLElementTagNameMap
}

const Item: React.FC<IItemProps> = ({ children, onChange, tag }) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const markRef = useRef<HTMLDivElement | null>(null)
  const isIntersectingRef = useRef<boolean>(true)
  const Tag: any = tag
  useEffect(() => {
    const observe = new IntersectionObserver(
      (changes) => {
        changes.forEach((change) => {
          if (isIntersectingRef.current !== change.isIntersecting) {
            batchedUpdates(() => {
              onChange(change.isIntersecting)
            })
          }
          isIntersectingRef.current = change.isIntersecting
        })
      },
      {
        rootMargin: '-1px 0px',
        root: rootRef.current!.parentElement,
      }
    )
    observe.observe(rootRef.current!)
    return () => {
      observe.disconnect()
    }
  }, [onChange])

  return (
    <Tag className="tw-float-left tw-relative" ref={rootRef}>
      {children}
    </Tag>
  )
}

interface IProps {
  itemCount: number
  children(params: {
    index: number
    isInMore?: boolean
    isCover?: boolean
    close?(): void
  }): ReactNode
  className?: string
  onTipShow?: IToolTipProps['onTipShow']
  renderMoreWrapper(contentCount: number): Wrapper
  renderMoreButton?(showTips: number): ReactNode
  activeIndex: number
  onChange?(moreIndexes: number[]): void
  listTag?: keyof HTMLElementTagNameMap
  itemTag?: keyof HTMLElementTagNameMap
}

const OverflowTooltip = ({
  itemCount,
  children,
  className,
  renderMoreWrapper,
  renderMoreButton,
  activeIndex,
  onTipShow,
  onChange,
  listTag = 'div',
  itemTag = 'div',
}: IProps) => {
  const [moreIndexes, setMoreIndexes] = useState<number[]>([])
  const moreIndexesRef = useRef<number[]>(moreIndexes)
  const isPauseRef = useRef(false)

  const handleChange = (index: number, isIntersecting: boolean) => {
    if (isPauseRef.current) return
    if (isIntersecting) {
      setMoreIndexes((prev) => {
        const i = prev.findIndex((d) => d === index)
        if (i !== -1) {
          prev.splice(i, 1)
        }
        return [...prev]
      })
    } else {
      setMoreIndexes((prev) => {
        if (prev.some((d) => d === index)) {
          return prev
        } else {
          return [...prev, index].sort((a, b) => a - b)
        }
      })
    }
  }

  const handleTipShow = useCallback(() => {
    isPauseRef.current = true
    if (onTipShow) onTipShow()
  }, [onTipShow])

  const handleTipClose = useCallback(() => {
    isPauseRef.current = false
  }, [])

  useEffect(() => {
    if (moreIndexesRef.current !== moreIndexes && onChange) {
      onChange(moreIndexes)
    }
    moreIndexesRef.current = moreIndexes
  }, [onChange, moreIndexes])

  const isActiveInMore = moreIndexes.includes(activeIndex)
  const ListTag: any = listTag
  return (
    <React.Fragment>
      <div className={classNames('tw-flex tw-items-center', className)}>
        <ListTag className="tw-overflow-hidden tw-h-full tw-pr-32">
          {Array.from({ length: itemCount }).map((item, index) => {
            return (
              <Item
                key={index}
                onChange={(isIntersecting) =>
                  handleChange(index, isIntersecting)
                }
                tag={itemTag}
              >
                {children({
                  index,
                  isCover: index === moreIndexes[0] - 1 && isActiveInMore,
                })}
                {index === moreIndexes[0] - 1 && isActiveInMore ? (
                  <div className="tw-absolute tw-inset-0 tw-whitespace-nowrap">
                    {children({ index: activeIndex })}
                  </div>
                ) : null}
              </Item>
            )
          })}
        </ListTag>
        {moreIndexes.length ? (
          <ToolTip
            theme={'light'}
            placement="bottom-end"
            arrowVisible={false}
            tag={'div'}
            content={({ close }) =>
              renderMoreWrapper(moreIndexes.length)(
                <React.Fragment>
                  {moreIndexes.map((item) => {
                    return (
                      <React.Fragment key={item}>
                        {children({
                          index:
                            item === activeIndex ? moreIndexes[0] - 1 : item,
                          isInMore: true,
                          close,
                        })}
                      </React.Fragment>
                    )
                  })}
                </React.Fragment>
              )
            }
            onTipShow={handleTipShow}
            onTipClose={handleTipClose}
          >
            {renderMoreButton ||
              ((showTips) => (
                <button
                  type="button"
                  className={classNames(
                    'tw-text-16 tw-border-1 tw-border-solid tw-h-32 tw-w-66 tw-rounded-2 hover:tw-bg-common-yellow hover:tw-bg-opacity-12 hover:tw-border-common-yellow',
                    {
                      'tw-bg-common-yellow tw-bg-opacity-12': showTips,
                    },
                    showTips
                      ? 'tw-border-common-yellow'
                      : 'tw-border-common-gray'
                  )}
                >
                  更多
                  <i
                    className={classNames(
                      'DCD_Icon',
                      showTips ? 'icon_triangle_up_12' : 'icon_triangle_down_12'
                    )}
                  />
                </button>
              ))}
          </ToolTip>
        ) : null}
      </div>
    </React.Fragment>
  )
}

export default OverflowTooltip
